import { useState, useContext, useEffect, useRef, useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AuthContext } from '../auth/AuthContext';
import api from '../api/axios';
import AutoGrowTextarea from './ui/AutoGrowTextarea';
import { getVisibleUsers } from '../api/userApi';
import { getProjects, getMyProjects } from '../api/projectApi';
import { getSprints } from '../api/sprintApi';
import { getEpicsForProject } from '../api/issueApi';
import toast from 'react-hot-toast';
import { getCustomTeams, getCustomRoles } from '../api/configApi';
import {
  mergeTeamsForDepartment,
  filterAssignableUsersByTeam,
  normalizeProjectMembersResponse,
} from '../utils/assigneeTeamFilter';
import { getDefaultTeamForRole, DEFAULT_DEPARTMENT } from '../constants/departments';
import { filterSprintsForIssuePicker } from '../constants/sprintAccess';
import { filterEpicsForSprintPicker } from '../utils/epicSprintFilter';
import { getDepartmentFormHints } from '../constants/departmentFormHints';
import {
  isQaTesterTeam,
  pickQaTeamAssigneeId,
  QA_TEAM_DEFAULT_ASSIGNEE_ID,
} from '../constants/teamDefaults';

const PRIORITIES = ['Blocker', 'Critical', 'High', 'Low'];
const DEFAULT_TYPES = ['Bug', 'Task', 'Story'];

const inputStyle = {
  width: '100%', padding: '0.6rem 0.8rem', border: '1px solid #e2e8f0',
  borderRadius: 8, fontSize: '0.9rem', background: '#f8fafc', boxSizing: 'border-box',
};
const labelStyle = {
  display: 'block', fontSize: '0.8rem', fontWeight: 600,
  color: '#374151', marginBottom: '0.25rem', marginTop: '0.75rem',
};

const toArray = (data, keys = []) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  for (const key of keys) {
    if (Array.isArray(data?.[key])) return data[key];
  }
  return [];
};

const extractUrls = (text) => {
  if (!text) return [];
  const matches = text.match(/https?:\/\/[^\s,]+/gi) || [];
  return Array.from(new Set(matches));
};

const composeDescription = (description, imageLinks) => {
  const cleanDescription = (description || '').trim();
  if (imageLinks.length === 0) return cleanDescription;
  const linksSection = imageLinks.map((url) => `- ${url}`).join('\n');
  return `${cleanDescription}\n\nImage Links:\n${linksSection}`.trim();
};

const storyTitle = (issue) => {
  const t = (issue?.description || '').split('\n')[0]?.trim();
  return t || `Story #${issue?.issueid}`;
};

const epicTitle = (epic) => {
  const t = (epic?.description || '').split('\n')[0]?.trim();
  return t || 'Epic';
};

const IssueForm = ({
  sprintId,
  projectId,
  parentId,
  fixedType,
  onSuccess,
  sprintList = [],
  allowSprintChange = false,
  sprintsEnabled = true,
  typeOptions = DEFAULT_TYPES,
  /** When set (e.g. sprint board epic filter), pre-select this epic as parent for Story/Task */
  defaultEpicId = null,
  /**
   * Creating a Task under a Story from the story modal: lock API parent_id to the story;
   * show Epic read-only when the story sits under that epic (cannot change hierarchy here).
   */
  taskParentStory = null,
  /**
   * Pre-selected issue type for the radio (e.g. "Story" for top-level New Task /
   * Add-to-Backlog flows; the "inside a Story" flow keeps using fixedType).
   * Ignored when not in availableTypes, when isBugOnly is true, or when fixedType
   * is set — fixedType always wins to preserve callers like Issues / MyTasks.
   */
  defaultType = null,
  /** Hide Bug/Task/Story radios — default true when fixedType is set. */
  hideTypePicker: hideTypePickerProp,
  /** My Tasks: stories assigned to the user in active sprints. */
  storyOptions = [],
  /** My Tasks: show story dropdown for Task creation under assigned stories. */
  enableStoryPicker = false,
  /** Use GET /projects/my-projects instead of full project list. */
  preferAssignedProjects = false,
  /** Description grows with content (Issues bug form). */
  autoGrowDescription = false,
}) => {
  const { role, user } = useContext(AuthContext);
  const formHints = useMemo(() => getDepartmentFormHints(user?.department), [user?.department]);
  const isBugOnly = fixedType === 'Bug';
  const hideTypePicker = hideTypePickerProp ?? Boolean(fixedType);
  const availableTypes = Array.isArray(typeOptions) && typeOptions.length > 0 ? typeOptions : DEFAULT_TYPES;
  const reporterName = user?.full_name || user?.email || 'You';

  /**
   * Initial radio selection. Priority:
   *   1. Bug-only mode → "Bug"
   *   2. fixedType (any caller-locked type) → fixedType
   *   3. defaultType (when valid) → defaultType
   *   4. First available type → fallback (mirrors prior "Task" default)
   */
  const resolveInitialType = () => {
    if (isBugOnly) return 'Bug';
    if (fixedType) return fixedType;
    if (defaultType && availableTypes.includes(defaultType)) return defaultType;
    return availableTypes[0] || 'Task';
  };

  const [form, setForm] = useState({
    projectid: projectId || '',
    sprint_id: sprintId || '',
    issuetype: resolveInitialType(),
    description: '',
    assigneeteam: 'Any',
    priority: 'High',
    assignee_id: '',
    story_points: '',
    due_date: '',
    parent_id: parentId || '',
    image_links: '',
  });

  const showStoryPicker =
    enableStoryPicker && !isBugOnly && !taskParentStory && fixedType === 'Task';

  const { data: customTeams = [] } = useQuery({ queryKey: ['custom-teams'], queryFn: getCustomTeams });
  const userDepartment = user?.department || DEFAULT_DEPARTMENT;
  const teamOptions = useMemo(
    () => mergeTeamsForDepartment(customTeams, userDepartment),
    [customTeams, userDepartment],
  );

  const assigneeDefaultsAppliedRef = useRef(false);

  // Default team + assignee once on load — do not reset after user picks another assignee.
  useEffect(() => {
    if (!user?.id || assigneeDefaultsAppliedRef.current) return;
    assigneeDefaultsAppliedRef.current = true;

    const allowed = new Set(teamOptions.map((t) => t.value));
    let userTeam = user.team || 'Any';
    if (!allowed.has(userTeam)) {
      userTeam =
        getDefaultTeamForRole(user.role, userDepartment) ||
        teamOptions.find((t) => t.value !== 'Any')?.value ||
        'Any';
    }
    const selfId = String(user.id);
    const assigneeId = isBugOnly && isQaTesterTeam(userTeam)
      ? String(QA_TEAM_DEFAULT_ASSIGNEE_ID)
      : selfId;
    setForm((prev) => ({
      ...prev,
      assigneeteam: userTeam,
      assignee_id: assigneeId,
    }));
  }, [user?.id, user?.team, user?.role, userDepartment, teamOptions, isBugOnly]);

  const { data: usersData } = useQuery({
    queryKey: ['users-visible', form.projectid],
    queryFn: () => getVisibleUsers(form.projectid || undefined).then((r) => r.data),
  });
  const users = toArray(usersData, ['users', 'rows', 'result']);

  // Fetch project members when project is selected (for assignee filtering)
  const { data: projectMembersData } = useQuery({
    queryKey: ['project-members', form.projectid],
    queryFn: () => api.get(`/projects/${form.projectid}/members`).then(r => r.data),
    enabled: !!form.projectid,
  });
  const projectMembers = normalizeProjectMembersResponse(projectMembersData);

  const { data: customRoles = [] } = useQuery({ queryKey: ['custom-roles'], queryFn: getCustomRoles });
  
  // Assigned projects for IC flows; stewards still use full project list unless preferAssignedProjects.
  const { data: projectsData } = useQuery({
    queryKey: preferAssignedProjects ? ['projects', 'my'] : ['projects', 'all'],
    queryFn: () => (preferAssignedProjects ? getMyProjects() : getProjects()).then((r) => r.data),
  });
  const projects = toArray(projectsData, ['projects', 'rows', 'result']);
  
  // Fetch sprints for selected project
  const { data: sprintsData } = useQuery({
    queryKey: ['sprints', form.projectid],
    queryFn: () => getSprints(form.projectid || null).then(r => r.data),
    enabled: !!form.projectid,
  });
  const sprints = toArray(sprintsData, ['sprints', 'rows', 'result']);

  const { data: epicsData = [], isPending: epicsLoading } = useQuery({
    queryKey: ['project-epics', form.projectid],
    queryFn: () => getEpicsForProject(form.projectid).then((r) => r.data),
    enabled: !!form.projectid && !isBugOnly,
  });
  const epics = useMemo(() => {
    const epicsRaw = toArray(epicsData, ['rows', 'result']);
    return filterEpicsForSprintPicker(epicsRaw, form.sprint_id);
  }, [epicsData, form.sprint_id]);
  
  const availableSprints = filterSprintsForIssuePicker(sprints, role, user?.permissions);

  const filteredStoryOptions = useMemo(() => {
    if (!showStoryPicker) return [];
    return storyOptions.filter((s) => {
      const pid = s.projectid ?? s.project_id;
      if (form.projectid && pid != null && String(pid) !== String(form.projectid)) return false;
      if (form.sprint_id && s.sprint_id != null && String(s.sprint_id) !== String(form.sprint_id)) return false;
      return true;
    });
  }, [showStoryPicker, storyOptions, form.projectid, form.sprint_id]);

  const selectedStory = useMemo(
    () => filteredStoryOptions.find((s) => String(s.issueid) === String(form.parent_id)) || null,
    [filteredStoryOptions, form.parent_id],
  );
  const isStoryParent = Boolean(selectedStory);
  const selectedEpic = useMemo(
    () => epics.find((e) => String(e.issueid) === String(form.parent_id)) || null,
    [epics, form.parent_id],
  );
  const isEpicParent = Boolean(selectedEpic) && !isStoryParent;
  const linkedEpic = useMemo(() => {
    if (selectedStory?.parent_id) {
      return epics.find((e) => String(e.issueid) === String(selectedStory.parent_id)) || null;
    }
    return isEpicParent ? selectedEpic : null;
  }, [selectedStory, epics, isEpicParent, selectedEpic]);
  
  // Reset sprint and assignee when project changes (not on initial mount)
  const prevProjectRef = useRef(projectId || '');
  const lockedStoryParentId = taskParentStory?.storyId != null ? String(taskParentStory.storyId) : '';
  useEffect(() => {
    if (form.projectid && form.projectid !== prevProjectRef.current) {
      // Project actually changed (not initial mount)
      if (!sprintId) {
        set('sprint_id', '');
      }
      // Reset assignee when project changes to avoid assigning to someone not in the new project
      set('assignee_id', '');
      if (!lockedStoryParentId) {
        set('parent_id', '');
      } else {
        set('parent_id', lockedStoryParentId);
      }
    }
    prevProjectRef.current = form.projectid;
  }, [form.projectid, lockedStoryParentId, sprintId]);

  const appliedDefaultEpic = useRef(false);
  useEffect(() => {
    appliedDefaultEpic.current = false;
  }, [defaultEpicId, form.projectid]);
  useEffect(() => {
    if (lockedStoryParentId) return;
    if (isBugOnly || appliedDefaultEpic.current || !defaultEpicId) return;
    if (!epics.some((e) => String(e.issueid) === String(defaultEpicId))) return;
    appliedDefaultEpic.current = true;
    setForm((prev) => ({ ...prev, parent_id: String(defaultEpicId) }));
  }, [isBugOnly, defaultEpicId, epics, lockedStoryParentId]);

  useEffect(() => {
    if (lockedStoryParentId || isBugOnly) return;
    if (!form.parent_id) return;
    const pid = String(form.parent_id);
    if (epics.some((e) => String(e.issueid) === pid)) return;
    if (showStoryPicker && storyOptions.some((s) => String(s.issueid) === pid)) return;
    setForm((prev) => (prev.parent_id === '' ? prev : { ...prev, parent_id: '' }));
  }, [epics, form.parent_id, lockedStoryParentId, isBugOnly, showStoryPicker, storyOptions]);

  useEffect(() => {
    if (!showStoryPicker || lockedStoryParentId || !form.parent_id) return;
    const pid = String(form.parent_id);
    if (epics.some((e) => String(e.issueid) === pid)) return;
    if (filteredStoryOptions.some((s) => String(s.issueid) === pid)) return;
    setForm((prev) => (prev.parent_id === '' ? prev : { ...prev, parent_id: '' }));
  }, [showStoryPicker, lockedStoryParentId, form.parent_id, filteredStoryOptions, epics]);

  useEffect(() => {
    if (!lockedStoryParentId) return;
    setForm((prev) =>
      prev.parent_id === lockedStoryParentId ? prev : { ...prev, parent_id: lockedStoryParentId },
    );
  }, [lockedStoryParentId]);

  useEffect(() => {
    if (isBugOnly) return;
    if (availableTypes.includes(form.issuetype)) return;
    // Current selection is no longer offered by typeOptions — pick the best
    // fallback in the same priority order as the initial state so the radio
    // stays aligned with caller intent.
    if (fixedType && availableTypes.includes(fixedType)) {
      set('issuetype', fixedType);
    } else if (defaultType && availableTypes.includes(defaultType)) {
      set('issuetype', defaultType);
    } else {
      set('issuetype', availableTypes[0]);
    }
  }, [isBugOnly, availableTypes, form.issuetype, fixedType, defaultType]);
  
  // Use project members for assignee dropdown when project is selected, otherwise fall back to all users
  const baseAssignableUsers = form.projectid && projectMembers.length > 0
    ? projectMembers.filter(u => !['admin', 'superadmin'].includes(u.role?.toLowerCase()))
    : users.filter(u => !['admin', 'superadmin'].includes(u.role?.toLowerCase()));
  const assignableUsers = filterAssignableUsersByTeam(baseAssignableUsers, form.assigneeteam, teamOptions);
  const imageLinks = extractUrls(form.image_links);

  // QA team defaults only when assignee is still empty — never override a chosen person.
  useEffect(() => {
    if (!isQaTesterTeam(form.assigneeteam) || form.assignee_id) return;
    if (isBugOnly) {
      const pick = pickQaTeamAssigneeId(assignableUsers);
      if (pick) {
        setForm((prev) => ({ ...prev, assignee_id: pick }));
      }
      return;
    }
    if (user?.id && assignableUsers.some((u) => String(u.id) === String(user.id))) {
      setForm((prev) => ({ ...prev, assignee_id: String(user.id) }));
    }
  }, [isBugOnly, form.assigneeteam, form.assignee_id, assignableUsers, user?.id]);

  const mutation = useMutation({
    mutationFn: (data) => api.post('/issues', {
      ...data,
      issuetype: isBugOnly ? 'Bug' : data.issuetype,
    }),
    // Hand the created issue back so a caller can keep working with it (e.g.
    // adding child tasks to a Story it just made). Callers that take no
    // argument are unaffected.
    onSuccess: (res) => onSuccess?.(res?.data?.issue ?? res?.data),
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to create issue'),
  });

  const set = (field, val) => setForm(prev => ({ ...prev, [field]: val }));

  const handleTeamChange = (team) => {
    set('assigneeteam', team);
    if (!isQaTesterTeam(team)) {
      set('assignee_id', '');
      return;
    }
    const pool = filterAssignableUsersByTeam(baseAssignableUsers, team, teamOptions);
    if (isBugOnly) {
      set('assignee_id', pickQaTeamAssigneeId(pool));
      return;
    }
    if (user?.id && pool.some((u) => String(u.id) === String(user.id))) {
      set('assignee_id', String(user.id));
      return;
    }
    set('assignee_id', pickQaTeamAssigneeId(pool));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const { image_links, ...rest } = form;
    const links = extractUrls(image_links);
    const payload = {
      ...rest,
      description: composeDescription(rest.description, links),
      parent_id: rest.parent_id ? Number(rest.parent_id) : undefined,
      assignee_id: rest.assignee_id ? Number(rest.assignee_id) : undefined,
    };
    mutation.mutate(payload);
  };

  return (
    <form onSubmit={handleSubmit}>
      <label style={labelStyle}>Project *</label>
      <select value={form.projectid} onChange={e => { set('projectid', e.target.value); set('sprint_id', ''); }} style={inputStyle} required>
        <option value="">Select project...</option>
        {projects.map(p => <option key={p.projectid} value={p.projectid}>{p.projectname}</option>)}
      </select>

      {(allowSprintChange || !sprintId) && sprintsEnabled && (
        <>
          <label style={labelStyle}>Sprint {availableSprints.length > 0 ? '*' : ''}</label>
          <select value={form.sprint_id} onChange={e => set('sprint_id', e.target.value)} style={inputStyle} required={availableSprints.length > 0}>
            <option value="">— No sprint (Backlog) —</option>
            {availableSprints.map(s => (
              <option key={s.id} value={s.id}>{s.sprint_name} ({s.status})</option>
            ))}
          </select>
          {form.projectid && availableSprints.length === 0 && (
            <p style={{ fontSize: '0.75rem', color: '#ef4444', marginTop: '0.25rem' }}>
              ⚠️ No active sprints available for this project. Create a sprint first.
            </p>
          )}
        </>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: isBugOnly ? '1fr 1fr' : '1fr', gap: '0.75rem' }}>
        {!isBugOnly && !hideTypePicker && (
          <div>
            <label style={labelStyle}>Type *</label>
            <div
              role="radiogroup"
              aria-label="Issue type"
              style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}
            >
              {availableTypes.map((t) => {
                const checked = form.issuetype === t;
                return (
                  <label
                    key={t}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '0.45rem 0.85rem',
                      borderRadius: 999,
                      border: `1.5px solid ${checked ? '#2C63AE' : '#cbd5e1'}`,
                      background: checked ? '#EAF6FD' : '#fff',
                      color: checked ? '#2C63AE' : '#475569',
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      cursor: 'pointer',
                      transition: 'background 0.12s, border-color 0.12s',
                      userSelect: 'none',
                    }}
                  >
                    <input
                      type="radio"
                      name="issuetype"
                      value={t}
                      checked={checked}
                      onChange={() => set('issuetype', t)}
                      style={{ accentColor: '#2C63AE', margin: 0 }}
                    />
                    {t}
                  </label>
                );
              })}
            </div>
          </div>
        )}
        {isBugOnly && (
          <div>
            <label style={labelStyle}>Priority *</label>
            <select value={form.priority} onChange={e => set('priority', e.target.value)} style={inputStyle} required>
              {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        )}
      </div>

      {!isBugOnly && form.projectid && lockedStoryParentId && taskParentStory && (
        <div
          style={{
            ...inputStyle,
            background: '#f1f5f9',
            border: '1px solid #cbd5e1',
            color: '#334155',
            fontSize: '0.85rem',
            lineHeight: 1.45,
          }}
        >
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', marginBottom: 6 }}>Hierarchy (fixed)</div>
          {taskParentStory.epic ? (
            <div style={{ marginBottom: 8 }}>
              <span style={{ fontWeight: 600, color: '#475569' }}>Epic: </span>
              #{taskParentStory.epic.id} · {taskParentStory.epic.label}
              {taskParentStory.epic.epic_status ? (
                <span style={{ color: '#64748b', fontWeight: 600 }}> ({taskParentStory.epic.epic_status})</span>
              ) : null}
            </div>
          ) : null}
          <div>
            <span style={{ fontWeight: 600, color: '#475569' }}>Parent story: </span>
            #{taskParentStory.storyId} · {taskParentStory.storyLabel}
          </div>
          <div style={{ marginTop: 8, fontSize: '0.72rem', color: '#64748b' }}>
            New task is created under this story. Epic and story cannot be changed here.
          </div>
        </div>
      )}

      {!isBugOnly && form.projectid && !lockedStoryParentId && (
        <>
          <label style={labelStyle}>
            Epic{' '}
            <span style={{ fontWeight: 400, color: '#94a3b8', fontSize: '0.72rem' }}>
              (optional — epics on this sprint only; backlog shows unassigned epics)
            </span>
          </label>
          {isStoryParent ? (
            linkedEpic ? (
              <div
                style={{
                  ...inputStyle,
                  background: '#f8fafc',
                  color: '#475569',
                  fontSize: '0.85rem',
                  cursor: 'default',
                }}
              >
                #{linkedEpic.issueid} · {epicTitle(linkedEpic)}
                {linkedEpic.epic_status ? (
                  <span style={{ color: '#64748b' }}> ({linkedEpic.epic_status})</span>
                ) : null}
                <span style={{ display: 'block', marginTop: 6, fontSize: '0.72rem', color: '#94a3b8' }}>
                  From selected story
                </span>
              </div>
            ) : (
              <div
                style={{
                  ...inputStyle,
                  background: '#f8fafc',
                  color: '#94a3b8',
                  fontSize: '0.85rem',
                  cursor: 'default',
                }}
              >
                — No epic on this story —
              </div>
            )
          ) : (
            <select
              value={isEpicParent ? form.parent_id : ''}
              onChange={(e) => set('parent_id', e.target.value)}
              style={inputStyle}
              disabled={epicsLoading}
            >
              <option value="">
                {epicsLoading
                  ? 'Loading epics…'
                  : epics.length === 0
                    ? form.sprint_id
                      ? 'No epics on this sprint'
                      : 'No backlog epics (no sprint) in this project'
                    : '— None —'}
              </option>
              {epics.map((ep) => {
                const title = (ep.description || '').split('\n')[0].trim() || 'Epic';
                const short = title.length > 72 ? `${title.slice(0, 72)}…` : title;
                return (
                  <option key={ep.issueid} value={ep.issueid}>
                    #{ep.issueid} · {short}
                    {ep.epic_status ? ` (${ep.epic_status})` : ''}
                  </option>
                );
              })}
            </select>
          )}
        </>
      )}

      {showStoryPicker && form.projectid && !lockedStoryParentId && (
        <>
          <label style={labelStyle}>
            Story{' '}
            <span style={{ fontWeight: 400, color: '#94a3b8', fontSize: '0.72rem' }}>
              (optional — your assigned stories in this sprint)
            </span>
          </label>
          <select
            value={isStoryParent ? form.parent_id : ''}
            onChange={(e) => set('parent_id', e.target.value)}
            style={inputStyle}
          >
            <option value="">
              {filteredStoryOptions.length === 0
                ? form.sprint_id
                  ? 'No assigned stories on this sprint'
                  : 'Select a sprint to see your stories'
                : '— Standalone task (no story) —'}
            </option>
            {filteredStoryOptions.map((s) => (
              <option key={s.issueid} value={s.issueid}>
                #{s.issueid} · {storyTitle(s)}
              </option>
            ))}
          </select>
        </>
      )}

      <label style={labelStyle}>Description *</label>
      {autoGrowDescription ? (
        <AutoGrowTextarea
          value={form.description}
          onChange={(e) => set('description', e.target.value)}
          minRows={3}
          style={inputStyle}
          placeholder={isBugOnly ? 'Describe the bug, steps to reproduce...' : formHints.issueWork}
          required
        />
      ) : (
        <textarea
          value={form.description}
          onChange={(e) => set('description', e.target.value)}
          rows={3}
          style={{ ...inputStyle, resize: 'vertical' }}
          placeholder={isBugOnly ? 'Describe the bug, steps to reproduce...' : formHints.issueWork}
          required
        />
      )}

      <>
        <label style={{ ...labelStyle, marginTop: '0.5rem' }}>Links / URLs <span style={{ fontWeight: 400, color: '#94a3b8', fontSize: '0.72rem' }}>(optional)</span></label>
        {autoGrowDescription ? (
          <AutoGrowTextarea
            value={form.image_links}
            onChange={(e) => set('image_links', e.target.value)}
            minRows={1}
            style={{ ...inputStyle, fontSize: '0.78rem', padding: '0.4rem 0.7rem' }}
            placeholder="Paste document URLs, image links, etc. (space or comma separated)"
          />
        ) : (
          <textarea
            value={form.image_links}
            onChange={(e) => set('image_links', e.target.value)}
            rows={1}
            style={{ ...inputStyle, resize: 'vertical', fontSize: '0.78rem', padding: '0.4rem 0.7rem' }}
            placeholder="Paste document URLs, image links, etc. (space or comma separated)"
          />
        )}
        {imageLinks.length > 0 && (
          <div style={{ marginTop: '0.6rem' }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#64748b', marginBottom: '0.45rem' }}>
              Detected Links ({imageLinks.length})
            </div>
            <div style={{ display: 'grid', gap: '0.3rem' }}>
              {imageLinks.map((url) => (
                <a key={url} href={url} target="_blank" rel="noreferrer"
                  style={{ color: '#2C63AE', textDecoration: 'underline', fontSize: '0.8rem', wordBreak: 'break-all' }}>
                  {url}
                </a>
              ))}
            </div>
          </div>
        )}
      </>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        <div>
          <label style={labelStyle}>Assign to Team *</label>
          <select
            value={form.assigneeteam}
            onChange={(e) => handleTeamChange(e.target.value)}
            style={inputStyle}
            required
          >
            {teamOptions.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Assign to Person *</label>
          <select value={form.assignee_id} onChange={e => set('assignee_id', e.target.value)} style={inputStyle} required>
            <option value="">{assignableUsers.length === 0 ? 'No members in this team' : 'Select person...'}</option>
            {assignableUsers.map(u => <option key={u.id} value={u.id}>{u.full_name || u.email}</option>)}
          </select>
          {form.assigneeteam !== 'Any' && assignableUsers.length === 0 && (
            <p style={{ fontSize: '0.72rem', color: '#f59e0b', marginTop: '0.2rem', margin: '3px 0 0' }}>
              No members assigned to {form.assigneeteam} team yet.
            </p>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        {!isBugOnly && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <label style={{ ...labelStyle, marginBottom: 0 }}>Story Points</label>
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <span 
                  title="Estimation Guide:
1 pt = Quick task (~0.5 day)
2 pts = Small task (~1 day)
3 pts = Medium task (~1.5 days)
5 pts = Large task (~2-3 days)
8 pts = Complex task (~3-5 days)
13+ pts = Very complex (break into smaller tasks)"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    background: '#2C8FC7',
                    color: '#fff',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    cursor: 'help',
                    userSelect: 'none'
                  }}
                >
                  ?
                </span>
              </div>
            </div>
            <input type="number" min={0} max={100} value={form.story_points}
              onChange={e => set('story_points', e.target.value)} style={inputStyle} placeholder="0" />
          </div>
        )}
        {!isBugOnly && (
          <div>
            <label style={labelStyle}>ETA <span style={{ fontWeight: 400, color: '#94a3b8', fontSize: '0.72rem' }}>(optional)</span></label>
            <input type="date" value={form.due_date}
              onChange={e => set('due_date', e.target.value)} style={inputStyle} />
          </div>
        )}
      </div>

      <label style={labelStyle}>{isBugOnly ? 'Reporter' : 'Creator'}</label>
      <div style={{
        ...inputStyle, display: 'flex', alignItems: 'center', gap: 8,
        background: '#f1f5f9', color: '#374151', cursor: 'default', userSelect: 'none',
      }}>
        <span style={{
          width: 26, height: 26, borderRadius: '50%', background: '#1272B3',
          color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '0.75rem', fontWeight: 700, flexShrink: 0,
        }}>
          {reporterName.charAt(0).toUpperCase()}
        </span>
        <span style={{ fontSize: '0.88rem', fontWeight: 600 }}>{reporterName}</span>
        <span style={{ marginLeft: 'auto', fontSize: '0.72rem', color: '#94a3b8' }}>auto-assigned</span>
      </div>

      <button type="submit" disabled={mutation.isPending} style={{
        marginTop: '1.2rem', width: '100%', padding: '0.8rem', background: '#1272B3',
        color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer',
      }}>
        {mutation.isPending ? 'Creating...' : `+ Create ${isBugOnly ? 'Bug' : form.issuetype}`}
      </button>
    </form>
  );
};

export default IssueForm;
